import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, History, Banknote, Layers, Repeat, AlertOctagon, ArrowDownUp, Coins } from "lucide-react";
import api from "../api/client";
import { formatUSD, formatNumber } from "../lib/format";

const NAV = [
    { to: "/admin", label: "Overview", testid: "admin-nav-overview", end: true },
    { to: "/admin/analytics", label: "Analytics", testid: "admin-nav-analytics" },
    { to: "/admin/users", label: "Users", testid: "admin-nav-users" },
    { to: "/admin/swaps", label: "Swaps", testid: "admin-nav-swaps" },
    { to: "/admin/deposits", label: "Deposits", testid: "admin-nav-deposits" },
    { to: "/admin/config", label: "Configuration", testid: "admin-nav-config" },
    { to: "/admin/fees", label: "Fees Collected", testid: "admin-nav-fees" },
    { to: "/admin/ads", label: "Ads", testid: "admin-nav-ads" },
    { to: "/admin/treasury", label: "Treasury", testid: "admin-nav-treasury" },
    { to: "/admin/settings", label: "Settings", testid: "admin-nav-settings" },
    { to: "/bots", label: "Bots", testid: "admin-nav-bots" },
];

export default function AdminDashboard() {
    const [d, setD] = useState(null);
    const [s, setS] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const [a, b] = await Promise.all([api.get("/admin/dashboard"), api.get("/admin/stats")]);
                setD(a.data); setS(b.data);
            } catch {}
        };
        load();
        const id = setInterval(load, 5000);
        return () => clearInterval(id);
    }, []);

    return (
        <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)] bg-grid-fine">
            <div className="max-w-[1480px] mx-auto px-4 lg:px-8 py-10 grid gap-6">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">Admin Console</div>
                    <h1 className="text-3xl font-medium tracking-tighter mt-2">Dashboard</h1>
                </div>

                <AdminTabs />

                {/* Reference-style 8-card layout */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Tile icon={Banknote} label="Notionals Value" value={d ? formatUSD(d.notionals_value, 0) : "—"} testid="d-notionals" />
                    <Tile icon={History} label="Trade History" value={d?.trade_history ?? "—"} testid="d-trade-history" />
                    <Tile icon={ArrowDownUp} label="User Transaction" value={d ? formatUSD(d.user_transaction, 2) : "—"} testid="d-user-tx" />
                    <Tile icon={Layers} label="Product" value={d?.products ?? "—"} testid="d-product" />
                    <Tile icon={Repeat} label="Swap History" value={d?.swap_history ?? "—"} testid="d-swap-history" />
                    <Tile icon={AlertOctagon} label="Liquidation" value={d?.liquidations ?? "—"} valueClass={(d?.liquidations || 0) > 0 ? "text-rose-400" : "text-zinc-100"} testid="d-liquidations" />
                    <SplitTile icon={ArrowDownUp} label="Transactions"
                        rows={[
                            { l: "Deposits", v: d ? formatUSD(d.deposits, 0) : "—", c: "text-emerald-400" },
                            { l: "Withdrawals", v: d ? formatUSD(d.withdrawals, 0) : "—", c: "text-rose-400" },
                        ]}
                        testid="d-tx-split"
                    />
                    <SplitTile icon={Coins} label="Margin"
                        rows={[
                            { l: "Initial", v: d ? formatUSD(d.initial_margin, 2) : "—", c: "text-zinc-100" },
                            { l: "Variable", v: d ? formatUSD(d.variable_margin, 2) : "—", c: "text-cyan-400" },
                        ]}
                        testid="d-margin-split"
                    />
                </div>

                {/* Extra protocol stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <Compact label="Users" value={s?.users?.total ?? "—"} sub={`${s?.users?.disabled ?? 0} disabled`} />
                    <Compact label="Active swaps" value={s?.swaps?.active ?? "—"} sub={`${s?.orders_open ?? 0} open orders`} />
                    <Compact label="Bots" value={`${s?.bots?.enabled ?? 0}/${s?.bots?.total ?? 0}`} sub="running / total" />
                    <Compact label="Oracle pairs" value={s?.oracle_coins ?? "—"} sub="Hyperliquid mainnet" valueClass="text-cyan-400" />
                    <Compact label="Open interest" value={s ? formatUSD(s.open_interest_usd, 0) : "—"} sub="notional locked" valueClass="text-cyan-400" />
                    <Compact label="Margin locked" value={s ? formatUSD(s.margin_locked_total, 0) : "—"} sub="across active swaps" valueClass="text-amber-400" />
                    <Compact label="Settled volume" value={s ? formatUSD(s.settled_volume_usd, 0) : "—"} sub={`${s?.swaps?.settled ?? 0} settlements`} />
                    <Compact label="Protocol fees" value={s ? formatUSD(s.protocol_fees_collected, 2) : "—"} sub={d ? `incl. ${formatUSD(d.liquidation_fees, 2)} liq` : "—"} valueClass="text-emerald-400" />
                </div>

                <div className="grid md:grid-cols-3 gap-2">
                    <QuickLink to="/admin/users" title="Manage users →" body="Credit / debit balances, freeze accounts, view active positions." />
                    <QuickLink to="/admin/swaps" title="Monitor swaps →" body="See every swap across all users. Force-settle stuck active swaps." />
                    <QuickLink to="/admin/config" title="Edit configuration →" body="Notification thresholds, participation limits, liquidation fees." />
                </div>
            </div>
        </div>
    );
}

export function AdminTabs() {
    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    return (
        <div className="flex flex-wrap gap-1 border-b border-[#1F1F24]" data-testid="admin-tabs">
            {NAV.map((n) => {
                const active = n.end ? path === n.to : path.startsWith(n.to);
                return (
                    <Link
                        key={n.to}
                        to={n.to}
                        data-testid={n.testid}
                        className={`px-4 py-2 text-[11px] font-mono uppercase tracking-[0.22em] border-b-2 -mb-px transition-colors ${
                            active ? "border-cyan-400 text-cyan-400" : "border-transparent text-zinc-500 hover:text-zinc-200"
                        }`}
                    >
                        {n.label}
                    </Link>
                );
            })}
        </div>
    );
}

function Tile({ icon: Icon, label, value, valueClass = "text-zinc-100", testid }) {
    return (
        <div className="bg-[#0A0A0C] border border-[#1F1F24] p-5 rounded-sm" data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">{label}</div>
                <Icon className="w-3.5 h-3.5 text-zinc-600" />
            </div>
            <div className={`mt-4 font-mono text-3xl tracking-tighter ${valueClass}`}>{value}</div>
        </div>
    );
}

function SplitTile({ icon: Icon, label, rows, testid }) {
    return (
        <div className="bg-[#0A0A0C] border border-[#1F1F24] p-5 rounded-sm" data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">{label}</div>
                <Icon className="w-3.5 h-3.5 text-zinc-600" />
            </div>
            <div className="mt-3 grid gap-1.5">
                {rows.map((r) => (
                    <div key={r.l} className="flex items-baseline justify-between">
                        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">{r.l}</span>
                        <span className={`font-mono text-base ${r.c}`}>{r.v}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Compact({ label, value, sub, valueClass = "text-zinc-100" }) {
    return (
        <div className="bg-[#0A0A0C] border border-[#1F1F24] p-4 rounded-sm">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">{label}</div>
            <div className={`mt-2 font-mono text-xl tracking-tighter ${valueClass}`}>{value}</div>
            <div className="mt-0.5 text-[10px] font-mono text-zinc-500">{sub}</div>
        </div>
    );
}

function QuickLink({ to, title, body }) {
    return (
        <Link to={to} className="bg-[#0A0A0C] border border-[#1F1F24] hover:border-cyan-500/40 p-5 rounded-sm transition-colors">
            <div className="text-sm font-medium tracking-tight">{title}</div>
            <p className="mt-2 text-xs text-zinc-400 leading-relaxed">{body}</p>
        </Link>
    );
}
